import { useState, useMemo } from "react";
import api from "@/lib/api";
import OnboardingBanner from "@/components/OnboardingBanner";
import { useExpenses, useExpensesSummary, useProperties, useInvalidate } from "@/lib/queries";
import { usePropertyFilter } from "@/context/PropertyFilter";
import { formatINR, formatDate, EXPENSE_CATEGORIES } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Plus, DownloadSimple, Trash, PencilSimple, Receipt, Funnel, UploadSimple, File, MagnifyingGlass, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const PIE_COLORS = ["#E8B931", "#2DD4BF", "#FB7185", "#60A5FA", "#FBBF24", "#A78BFA", "#F472B6", "#34D399", "#FB923C", "#818CF8", "#E879F9", "#4ADE80", "#FCA5A5"];

export default function Expenses() {
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [editExp, setEditExp]           = useState(null);
  const [form, setForm]                 = useState(defaultForm());
  const { selectedPropertyId, selectedRoomId } = usePropertyFilter();
  const [filterCat, setFilterCat]       = useState("all");
  const [filterMonth, setFilterMonth]   = useState("all");
  const [searchQuery, setSearchQuery]   = useState("");
  const [receiptUploading, setReceiptUploading] = useState(false);

  const expFilters = {
    property_id: selectedPropertyId || undefined,
    room_id:     selectedRoomId     || undefined,
    category:    filterCat !== "all" ? filterCat : undefined,
  };
  const { data: expenses = [], isLoading: loading, isFetching: refreshing } = useExpenses(expFilters);
  const { data: properties = [] } = useProperties();
  const { data: summaryData } = useExpensesSummary(selectedPropertyId ? { property_id: selectedPropertyId } : {});
  const categorySummary = summaryData?.breakdown || [];
  const invalidate = useInvalidate();

  // Client-side filtering for month + search
  const filteredExpenses = useMemo(() => {
    let result = expenses;
    if (filterMonth !== "all") result = result.filter(e => e.date?.startsWith(filterMonth));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(e =>
        (e.description || "").toLowerCase().includes(q) ||
        (e.category || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [expenses, filterMonth, searchQuery]);

  // Monthly trend — last 6 months
  const monthlyTrend = useMemo(() => {
    const map = {};
    expenses.forEach(e => {
      if (!e.date) return;
      const m = e.date.slice(0, 7);
      map[m] = (map[m] || 0) + (e.amount || 0);
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, amount]) => ({
        month: new Date(month + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" }),
        amount,
      }));
  }, [expenses]);

  // Available months for dropdown
  const availableMonths = useMemo(() =>
    [...new Set(expenses.map(e => e.date?.slice(0, 7)).filter(Boolean))].sort().reverse()
  , [expenses]);

  const openAdd  = () => { setEditExp(null); setForm(defaultForm()); setDialogOpen(true); };
  const openEdit = (exp) => { setEditExp(exp); setForm({ ...exp }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editExp) {
        await api.put(`/expenses/${editExp.id}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setDialogOpen(false);
      invalidate.afterExpenseMutation();
    } catch { toast.error("Failed to save expense"); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      invalidate.afterExpenseMutation();
    } catch { toast.error("Failed to delete"); }
  };

  const handleExport = (format) => {
    const params = new URLSearchParams({ format });
    if (selectedPropertyId) params.append("property_id", selectedPropertyId);
    window.open(`${api.defaults.baseURL}/export/expenses?${params}`, "_blank");
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setReceiptUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, receipt_path: data.storage_path, receipt_name: file.name }));
      toast.success("Receipt uploaded!");
    } catch { toast.error("Upload failed"); }
    finally { setReceiptUploading(false); }
  };

  const propMap = {};
  properties.forEach(p => { propMap[p.property_id || p.id] = p.name; });

  const totalExpenses  = filteredExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  // GST Eligible card shows the CLAIMABLE GST (input tax credit) embedded in each eligible
  // expense — amounts are treated as GST-inclusive: embedded GST = amount × rate/(100+rate).
  const gstItems       = filteredExpenses.filter(e => e.gst_eligible);
  const gstEligibleBase = gstItems.reduce((s, e) => s + (e.amount || 0), 0);
  const gstEligible    = Math.round(gstItems.reduce((s, e) => {
    const rate = e.gst_rate || 18;
    return s + (e.amount || 0) * rate / (100 + rate);
  }, 0));
  const taxDeductible  = filteredExpenses.filter(e => e.tax_deductible).reduce((s, e) => s + (e.amount || 0), 0);
  const hasActiveFilter = filterMonth !== "all" || searchQuery.trim();

  const inputCls        = "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-400 mt-1 focus:border-amber-400 focus:ring-amber-400";
  const selectTriggerCls = "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 mt-1";
  const btnCancel       = "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 font-medium rounded-xl px-5 py-2.5 text-sm transition-colors";
  const btnPrimary      = "bg-amber-400 text-white hover:bg-amber-500 font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors";
  const tooltipStyle    = { backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", fontSize: "11px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" };

  if (loading) return (
    <div className="animate-pulse space-y-4">
      <div className="h-8 w-48 bg-slate-200 rounded" />
      <div className="h-64 bg-slate-100 rounded-2xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <OnboardingBanner pageKey="expenses" />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-manrope text-2xl md:text-3xl font-semibold text-slate-900 dark:text-amber-400 tracking-tight">Expenses</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            {filteredExpenses.length} expense{filteredExpenses.length !== 1 ? "s" : ""}
            {filteredExpenses.length !== expenses.length && ` (filtered from ${expenses.length})`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => handleExport("csv")}
            className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 font-medium rounded-xl px-3 py-2 text-xs flex items-center gap-1.5 transition-colors"
            data-testid="export-csv-button">
            <DownloadSimple size={14} /> CSV
          </button>
          <button onClick={() => handleExport("pdf")}
            className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 font-medium rounded-xl px-3 py-2 text-xs flex items-center gap-1.5 transition-colors"
            data-testid="export-pdf-button">
            <DownloadSimple size={14} /> PDF
          </button>
          <button onClick={openAdd}
            className="bg-amber-400 text-white hover:bg-amber-500 font-semibold rounded-xl px-4 py-2 text-sm flex items-center gap-2 transition-colors shadow-sm"
            data-testid="add-expense-button">
            <Plus size={16} weight="bold" /> Add
          </button>
        </div>
      </div>

      {/* Summary Cards — reflect current filter */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-2xl shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Total</p>
            <p className="font-mono text-lg md:text-xl font-semibold text-red-500 mt-1">{formatINR(totalExpenses)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-2xl shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">GST Claimable (ITC)</p>
            <p className="font-mono text-lg md:text-xl font-semibold text-emerald-500 mt-1">{formatINR(gstEligible)}</p>
            {gstEligibleBase > 0 && (
              <p className="text-[10px] text-slate-400 mt-0.5">on {formatINR(gstEligibleBase)} eligible spend</p>
            )}
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-2xl shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Tax Deductible</p>
            <p className="font-mono text-lg md:text-xl font-semibold text-sky-500 mt-1">{formatINR(taxDeductible)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      {expenses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categorySummary.length > 0 && (
            <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-2xl shadow-sm" data-testid="expense-pie-chart">
              <CardHeader className="pb-2">
                <CardTitle className="font-manrope text-sm font-semibold text-slate-800 dark:text-white">By Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  {/* Fixed-size wrapper — ResponsiveContainer with width:100% collapses to 0 inside a flex row */}
                  <div className="w-[160px] h-[160px] shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        {/* isAnimationActive=false: the mount animation freezes at ~1deg sectors when
                            rAF is throttled (background/inactive tab), leaving the donut invisible */}
                        <Pie data={categorySummary} dataKey="amount" nameKey="category" cx="50%" cy="50%"
                          outerRadius={70} innerRadius={35} paddingAngle={2} strokeWidth={0} isAnimationActive={false}>
                          {categorySummary.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} formatter={v => [formatINR(v)]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {categorySummary.slice(0, 6).map((item, i) => (
                      <div key={item.category} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <span className="text-slate-600 dark:text-slate-300 truncate max-w-[80px]">{item.category}</span>
                        </div>
                        <span className="font-mono text-slate-500 dark:text-slate-400">{formatINR(item.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {monthlyTrend.length > 1 && (
            <Card className="bg-white dark:bg-slate-800 border-slate-200 rounded-2xl shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="font-manrope text-sm font-semibold text-slate-800 dark:text-white">Monthly Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={monthlyTrend} barSize={22}>
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip contentStyle={tooltipStyle} formatter={v => [formatINR(v), "Expenses"]} />
                    <Bar dataKey="amount" fill="#E8B931" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Funnel size={15} className="text-slate-400 shrink-0" />
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-36 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs h-9">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
            <SelectItem value="all">All Categories</SelectItem>
            {EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger className="w-36 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs h-9">
            <SelectValue placeholder="All Months" />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
            <SelectItem value="all">All Months</SelectItem>
            {availableMonths.map(m => (
              <SelectItem key={m} value={m}>
                {new Date(m + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input type="text" placeholder="Search description or category…"
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:border-amber-400 h-9" />
        </div>

        {hasActiveFilter && (
          <button onClick={() => { setFilterMonth("all"); setSearchQuery(""); }}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-400 transition-colors px-1">
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Expense List */}
      <div className="space-y-2" data-testid="expense-list">
        {refreshing && (
          <div className="flex items-center gap-2 text-xs text-slate-400 py-1">
            <div className="w-3 h-3 border-2 border-slate-300 border-t-amber-400 rounded-full animate-spin" />
            Updating...
          </div>
        )}
        {filteredExpenses.map((exp) => (
          <div key={exp.id} className="bg-white dark:bg-slate-800 border border-slate-200 rounded-xl p-4 flex items-center gap-4 shadow-sm">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
              <Receipt size={18} className="text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm text-slate-800 dark:text-white font-semibold truncate">{exp.description || exp.category}</p>
                <Badge variant="outline" className="bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 text-[10px] shrink-0">{exp.category}</Badge>
              </div>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400">
                <span>{formatDate(exp.date)}</span>
                <span>{propMap[exp.property_id] || "General"}</span>
              </div>
              <div className="flex gap-1.5 mt-1.5">
                {exp.gst_eligible    && <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px] px-1.5 py-0">GST</Badge>}
                {exp.tax_deductible  && <Badge className="bg-sky-50 text-sky-600 border-sky-200 text-[9px] px-1.5 py-0">Tax</Badge>}
                {exp.is_personal     && <Badge className="bg-purple-50 text-purple-600 border-purple-200 text-[9px] px-1.5 py-0">Personal</Badge>}
                {exp.receipt_path    && <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[9px] px-1.5 py-0 flex items-center gap-0.5"><File size={8} /> Receipt</Badge>}
              </div>
            </div>
            <p className="font-mono text-sm text-red-500 font-semibold shrink-0">{formatINR(exp.amount)}</p>
            <div className="flex gap-1 shrink-0">
              <button onClick={e => { e.stopPropagation(); openEdit(exp); }} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"><PencilSimple size={14} /></button>
              <button onClick={e => { e.stopPropagation(); handleDelete(exp.id); }} className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"><Trash size={14} /></button>
            </div>
          </div>
        ))}
        {filteredExpenses.length === 0 && (
          <p className="text-slate-400 text-sm text-center py-12">
            {expenses.length === 0 ? "No expenses recorded yet." : "No expenses match your filters."}
          </p>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 max-w-md shadow-xl">
          <DialogHeader>
            <DialogTitle className="font-manrope text-slate-900 dark:text-white">{editExp ? "Edit Expense" : "Add Expense"}</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">Log an expense for your property.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Property</Label>
              <Select value={form.property_id || ""} onValueChange={v => setForm(f => ({ ...f, property_id: v }))}>
                <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                  {properties.map(p => <SelectItem key={p.property_id || p.id} value={p.property_id || p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Amount (INR)</Label>
                <Input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className={inputCls} data-testid="expense-amount-input" />
              </div>
              <div>
                <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className={inputCls} />
              </div>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Category</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className={selectTriggerCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-200">
                  {EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inputCls} placeholder="Plumber visit for leaky faucet" />
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                <Checkbox checked={form.gst_eligible} onCheckedChange={v => setForm(f => ({ ...f, gst_eligible: v }))} className="border-slate-300" /> GST Eligible
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                <Checkbox checked={form.tax_deductible} onCheckedChange={v => setForm(f => ({ ...f, tax_deductible: v }))} className="border-slate-300" /> Tax Deductible
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                <Checkbox checked={form.is_personal} onCheckedChange={v => setForm(f => ({ ...f, is_personal: v }))} className="border-slate-300" /> Personal
              </label>
            </div>
            {form.gst_eligible && (
              <div className="flex items-center gap-2 flex-wrap pt-1">
                <span className="text-xs text-slate-600 dark:text-slate-300 font-semibold">GST rate:</span>
                {[5, 12, 18, 28].map(r => (
                  <button key={r} type="button" onClick={() => setForm(f => ({ ...f, gst_rate: r }))}
                    className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${(form.gst_rate || 18) === r
                      ? "bg-emerald-500 text-white border-emerald-500"
                      : "bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600"}`}>
                    {r}%
                  </button>
                ))}
                <span className="text-[10px] text-slate-500 dark:text-slate-400">
                  ≈ {formatINR(Math.round((parseFloat(form.amount) || 0) * (form.gst_rate || 18) / (100 + (form.gst_rate || 18))))} claimable
                </span>
              </div>
            )}
            <div>
              <Label className="text-slate-600 dark:text-slate-300 text-xs font-semibold">Receipt</Label>
              {form.receipt_path ? (
                <div className="flex items-center gap-2 mt-1 p-2 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                  <File size={16} className="text-amber-500" />
                  <span className="text-xs text-slate-700 dark:text-slate-200 truncate flex-1">{form.receipt_name || "Receipt attached"}</span>
                  <button onClick={() => setForm(f => ({ ...f, receipt_path: "", receipt_name: "" }))} className="text-slate-400 hover:text-red-500 transition-colors text-xs">Remove</button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 mt-1 px-3 py-2.5 bg-slate-50 dark:bg-slate-700 border border-dashed border-slate-200 dark:border-slate-600 rounded-xl text-xs text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer transition-colors" data-testid="receipt-upload-label">
                  {receiptUploading
                    ? <><div className="w-3.5 h-3.5 border-2 border-slate-300 border-t-amber-400 rounded-full animate-spin" /> Uploading...</>
                    : <><UploadSimple size={14} /> Upload Receipt (image or PDF)</>}
                  <input type="file" accept="image/*,.pdf" onChange={handleReceiptUpload} className="hidden" disabled={receiptUploading} data-testid="receipt-upload-input" />
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className={btnCancel}>Cancel</button>
            <button onClick={handleSave} className={btnPrimary} data-testid="save-expense-button">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function defaultForm() {
  return {
    property_id: "", amount: "", date: new Date().toISOString().slice(0, 10),
    category: "Other", description: "", gst_eligible: false, tax_deductible: false, is_personal: false,
    gst_rate: 18,
  };
}
